import React, { useState, useEffect, useRef } from "react";
import { IMediaItem } from '@src/definitions/media';

import "./image.scss";
import { canUseMediaHandler, getMediaHandlerUrl } from './image-utils';

interface Props extends IAwsMediaEdits {
    mediaItem: IMediaItem;
    className?: string;
    progressiveImageLoading?: boolean;
    lazyLoad?: boolean;
    priority?: "high" | "low" | "auto";
    onClick?(): any;
}

export interface IAwsMediaEdits {
    width?: number;
    height?: number;
    fit?: "cover" |  "contain" |  "fill" |  "inside" | "outside";
}

const Image = (props: Props) => {
    const {
        mediaItem,
        progressiveImageLoading = true,
        lazyLoad = false,
        priority = "auto",
        className = "",
        onClick = () => {},
        ...options
    } = props;

    const [imageLoaded, setImageLoaded] = useState(false);
    const [placeholderLoaded, setPlaceholderLoaded] = useState(false);

    const [useFallbackSrc, setUseFallbackSrc] = useState(false);
    const imageRef = useRef<HTMLImageElement>(null);
    const placeholderRef = useRef<HTMLImageElement>(null);

    const useMediaHandler = canUseMediaHandler(mediaItem.src) && !useFallbackSrc;
    const isReady = imageLoaded ? " is-ready" : "";
    const placeholderIsReady = placeholderLoaded ? " is-ready" : "";
    const placeholderOptions = { ...options, width: 10 };

    // Use top padding to ensure that the Image component is at least the height of the image to be loaded
    const paddingTop = (mediaItem.height / mediaItem.width * 100) + "%";

    const src = useMediaHandler
        ? getMediaHandlerUrl(mediaItem.src, options)
        : mediaItem.src;

    const placeholderSrc = useMediaHandler && progressiveImageLoading
        ? getMediaHandlerUrl(mediaItem.src, placeholderOptions)
        : src;

    // Check if image is already in browser cache
    useEffect(() => {
        // For placeholder
        if (placeholderRef.current && placeholderRef.current.complete) {
            setPlaceholderLoaded(true);
        }

        // For main image
        if (imageRef.current) {
            if (imageRef.current.complete && imageRef.current.naturalHeight !== 0) {
                // Image is already loaded from cache
                setImageLoaded(true);
            }
        }
    }, [src, placeholderSrc]);

    const handlePlaceholderLoad = () => {
        setPlaceholderLoaded(true);
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    const handleImageError = () => {
        if (useMediaHandler) {
            setUseFallbackSrc(true);
        }
    };

    return (
        <div className={`Image ${className}`} onClick={onClick} onContextMenu={(event) => event.preventDefault()}>
            <div style={{ paddingTop }}></div>
            {progressiveImageLoading && (
                <img
                    ref={placeholderRef}
                    className={`Image__placeholder ${placeholderIsReady}`}
                    src={placeholderSrc}
                    alt=""
                    onLoad={handlePlaceholderLoad}
                    onError={handleImageError}
                />
            )}
            <img
                ref={imageRef}
                className={`Image__target ${isReady}`}
                src={src}
                alt=""
                onLoad={handleImageLoad}
                onError={handleImageError}
            />
        </div>
    );
};

export default Image;
